/**
 * "Your devices": the devices signed in to this account, with a sign-out
 * control for each one and one control that signs out all other devices.
 * Sign-out of the device in use goes through the normal logout, which also
 * clears this browser and opens the login page.
 */

import { MonitorSmartphone } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SkeletonRows } from "@/components/ui/skeleton";
import { useLogout } from "@/features/auth/hooks/use-logout";
import { formatRelativeTime } from "@/features/realtor/utils/relative-time";
import { StudioButton } from "@/features/studio/theme";
import {
	useDeviceSessions,
	useRevokeDeviceSession,
	useRevokeOtherDeviceSessions,
} from "../hooks/use-device-sessions";
import {
	type DeviceSession,
	getErrorStatus,
} from "../services/sessions-api.service";

/** What the confirm dialog signs out. */
type SignOutTarget =
	| { kind: "device"; session: DeviceSession }
	| { kind: "current" }
	| { kind: "others" };

export function DeviceSessions() {
	const { t, i18n } = useTranslation();
	const sessionsQuery = useDeviceSessions();
	const revoke = useRevokeDeviceSession();
	const revokeOthers = useRevokeOtherDeviceSessions();
	const { logout, isLoggingOut } = useLogout();
	const [target, setTarget] = useState<SignOutTarget | null>(null);
	const inFlight = useRef(false);

	const sessions = sessionsQuery.data ?? [];
	const hasCurrent = sessions.some((session) => session.current);
	const otherCount = sessions.filter((session) => !session.current).length;
	const deviceName = (session: DeviceSession) =>
		session.deviceLabel || t("settings.devices.unknownDevice");
	const busy = revoke.isPending || revokeOthers.isPending || isLoggingOut;

	const finish = () => {
		inFlight.current = false;
		setTarget(null);
	};

	const confirmSignOut = () => {
		if (!target || inFlight.current) return;
		inFlight.current = true;
		if (target.kind === "current") {
			// useLogout always ends on /login, so this component unmounts.
			void logout();
			return;
		}
		if (target.kind === "others") {
			revokeOthers.mutate(undefined, {
				onSuccess: (count) =>
					toast.success(t("settings.devices.signedOutOthersToast", { count })),
				onError: () => toast.error(t("settings.devices.signOutOthersFailed")),
				onSettled: finish,
			});
			return;
		}
		revoke.mutate(target.session.id, {
			onSuccess: () => toast.success(t("settings.devices.signedOutToast")),
			onError: (error) => {
				if (getErrorStatus(error) === 404) {
					toast.info(t("settings.devices.alreadySignedOut"));
				} else {
					toast.error(t("settings.devices.signOutFailed"));
				}
			},
			onSettled: finish,
		});
	};

	const dialogTitle =
		target?.kind === "others"
			? t("settings.devices.signOutOthersTitle")
			: target?.kind === "current"
				? t("settings.devices.signOutCurrentTitle")
				: t("settings.devices.signOutTitle");
	const dialogBody =
		target?.kind === "others"
			? t("settings.devices.signOutOthersBody", { count: otherCount })
			: target?.kind === "current"
				? t("settings.devices.signOutCurrentBody")
				: target
					? t("settings.devices.signOutBody", {
							device: deviceName(target.session),
						})
					: null;

	return (
		<div className="space-y-4">
			<div className="flex items-center gap-2 border-b border-[var(--color-dashboard-border)] pb-3">
				<MonitorSmartphone className="h-5 w-5 text-[var(--color-dashboard-accent-dark)]" />
				<h3
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 600,
						fontSize: 16,
						color: "var(--color-dashboard-text)",
					}}
				>
					{t("settings.devices.title")}
				</h3>
			</div>
			<p className="text-sm text-muted-foreground">
				{t("settings.devices.description")}
			</p>

			{sessionsQuery.isPending ? (
				<SkeletonRows label={t("settings.devices.title")} rows={2} />
			) : sessionsQuery.isError ? (
				<div className="py-4 text-center">
					<p className="mb-4 text-sm text-dashboard-pill-risk">
						{t("settings.devices.loadFailed")}
					</p>
					<StudioButton
						variant="quiet"
						size="sm"
						onClick={() => sessionsQuery.refetch()}
					>
						{t("settings.devices.retry")}
					</StudioButton>
				</div>
			) : sessions.length === 0 ? (
				<p className="py-4 text-center text-sm text-muted-foreground">
					{t("settings.devices.empty")}
				</p>
			) : (
				<ul className="space-y-3">
					{sessions.map((session) => (
						<li
							key={session.id}
							className="flex items-center justify-between gap-3 rounded-xl border border-[var(--color-dashboard-border)] bg-[var(--color-dashboard-paper)] p-4"
						>
							<div className="min-w-0 space-y-1">
								<div className="flex flex-wrap items-center gap-2">
									<p className="text-sm font-semibold text-[var(--color-dashboard-text)]">
										{deviceName(session)}
									</p>
									{session.current && (
										<span className="rounded-full bg-dashboard-pill-done-bg px-2 py-0.5 text-xs font-semibold text-dashboard-success-strong">
											{t("settings.devices.thisDevice")}
										</span>
									)}
								</div>
								<p className="text-sm text-muted-foreground">
									{t("settings.devices.lastUsed", {
										when: formatRelativeTime(
											session.lastUsedAt,
											i18n.language,
											t("settings.devices.justNow"),
										),
									})}
								</p>
							</div>
							<StudioButton
								variant="quiet"
								size="sm"
								aria-label={
									session.current
										? t("settings.devices.signOutCurrentFor", {
												device: deviceName(session),
											})
										: t("settings.devices.signOutFor", {
												device: deviceName(session),
											})
								}
								onClick={() =>
									setTarget(
										session.current
											? { kind: "current" }
											: { kind: "device", session },
									)
								}
							>
								{t("settings.devices.signOut")}
							</StudioButton>
						</li>
					))}
				</ul>
			)}

			{sessionsQuery.isSuccess && hasCurrent && otherCount > 0 && (
				<div className="flex justify-end">
					<StudioButton
						variant="quiet"
						size="sm"
						onClick={() => setTarget({ kind: "others" })}
					>
						{t("settings.devices.signOutOthers")}
					</StudioButton>
				</div>
			)}

			<AlertDialog
				open={target !== null}
				onOpenChange={(open) => {
					if (!open && !busy) setTarget(null);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{dialogTitle}</AlertDialogTitle>
						<AlertDialogDescription>
							{dialogBody}
							{target?.kind === "device" && !hasCurrent && (
								<span className="mt-2 block text-dashboard-pill-awaiting">
									{t("settings.devices.currentUnknownWarning")}
								</span>
							)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={busy}>
							{t("settings.devices.signOutCancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							disabled={busy}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
							onClick={(event) => {
								event.preventDefault();
								confirmSignOut();
							}}
						>
							{t("settings.devices.signOutConfirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
